/**
 * Poll de manifest.json en readiness=preview.
 * - Upgrade a diagnostic → onUpgrade (para)
 * - Crecimiento preview (nuevas series/instancias, mismo readiness) → onPreviewUpdate
 * Usa If-None-Match cuando hay ETag.
 *
 * En urgencia: poll agresivo al inicio (`fastIntervalMs` durante `fastWindowMs`),
 * luego intervalo normal.
 */

import { resolveManifestReadiness } from './manifestNormalize'

/**
 * Huella estable del contenido preview (series + instancias).
 * Sirve para detectar crecimiento sin depender solo del ETag.
 * @param {object|null|undefined} manifest
 * @returns {string}
 */
/**
 * Conteo series/instancias del manifest (preview o diagnostic).
 * @param {object|null|undefined} manifest
 * @returns {{ series: number, instances: number }}
 */
export function countManifestSeriesAndInstances(manifest) {
  if (!manifest || typeof manifest !== 'object') {
    return { series: 0, instances: 0 }
  }
  const series = Array.isArray(manifest.series) ? manifest.series : []
  let instances = 0
  for (let i = 0; i < series.length; i++) {
    const s = series[i] || {}
    instances += Array.isArray(s.instances) ? s.instances.length : 0
  }
  return { series: series.length, instances }
}

export function manifestPreviewFingerprint(manifest) {
  if (!manifest || typeof manifest !== 'object') return ''
  const series = Array.isArray(manifest.series) ? manifest.series : []
  const parts = []
  let totalInst = 0
  for (let i = 0; i < series.length; i++) {
    const s = series[i] || {}
    const uid = String(
      s.seriesInstanceUID || s.SeriesInstanceUID || s.seriesUID || ''
    )
    const n = Array.isArray(s.instances) ? s.instances.length : 0
    totalInst += n
    parts.push(`${uid}:${n}`)
  }
  return `${series.length}|${totalInst}|${parts.join(',')}`
}

/**
 * @param {object} opts
 * @param {string} opts.manifestUrl
 * @param {(manifest: object, meta: { etag?: string }) => void} [opts.onUpgrade]
 * @param {(manifest: object, meta: { etag?: string, fingerprint: string }) => void} [opts.onPreviewUpdate]
 * @param {number} [opts.intervalMs=8000]
 * @param {number} [opts.fastIntervalMs=2500] intervalo inicial agresivo
 * @param {number} [opts.fastWindowMs=45000] ms con poll rápido desde el start
 * @param {() => boolean} [opts.shouldContinue]
 * @param {string} [opts.etag]
 * @param {string} [opts.initialFingerprint]
 * @returns {() => void} stop
 */
export function startManifestPreviewPoll(opts) {
  const slowMs = opts.intervalMs > 0 ? opts.intervalMs : 8000
  const fastMs =
    opts.fastIntervalMs > 0 ? opts.fastIntervalMs : Math.min(2500, slowMs)
  const fastWindowMs = opts.fastWindowMs > 0 ? opts.fastWindowMs : 45000
  const startedAt = Date.now()
  let etag = opts.etag || null
  let lastFp = opts.initialFingerprint || ''
  let stopped = false
  let timer = null
  let inFlight = false

  const currentInterval = () =>
    Date.now() - startedAt < fastWindowMs ? fastMs : slowMs

  const scheduleNext = () => {
    if (stopped) return
    if (timer) clearTimeout(timer)
    timer = setTimeout(() => {
      void tick().finally(() => {
        scheduleNext()
      })
    }, currentInterval())
  }

  const tick = async () => {
    if (stopped || inFlight) return
    if (opts.shouldContinue && !opts.shouldContinue()) {
      stop()
      return
    }
    inFlight = true
    try {
      const headers = {}
      if (etag) headers['If-None-Match'] = etag
      const res = await fetch(opts.manifestUrl, {
        method: 'GET',
        headers,
        cache: 'no-store',
      })
      if (res.status === 304) return
      if (!res.ok) return
      const nextEtag = res.headers.get('ETag') || res.headers.get('etag')
      if (nextEtag) etag = nextEtag
      const json = await res.json()
      const readiness = resolveManifestReadiness(json)
      if (readiness === 'diagnostic') {
        if (typeof opts.onUpgrade === 'function') {
          opts.onUpgrade(json, { etag: nextEtag || undefined })
        }
        stop()
        return
      }
      if (readiness === 'preview' && typeof opts.onPreviewUpdate === 'function') {
        const fp = manifestPreviewFingerprint(json)
        if (fp && fp !== lastFp) {
          lastFp = fp
          opts.onPreviewUpdate(json, {
            etag: nextEtag || undefined,
            fingerprint: fp,
          })
        }
      }
    } catch (_) {
      /* red guest / CORS: silencioso; R2 preview sigue válido */
    } finally {
      inFlight = false
    }
  }

  const stop = () => {
    stopped = true
    if (timer) {
      clearTimeout(timer)
      timer = null
    }
  }

  timer = setTimeout(() => {
    void tick().finally(() => {
      scheduleNext()
    })
  }, 800)
  return stop
}
